import { useState } from 'react';
import { useLang } from '../i18n/lang';
import { refusalWords } from '../i18n/refusal';
import { DISPLAY_NAME_MAX } from '@versesverse/core';

/** What the account control reads off the social client. */
export interface AccountClient {
  signOut(): Promise<void>;
  /** What this reader is called here — see the control below for why it is theirs to set. */
  renameMe(displayName: string): Promise<{ displayName: string }>;
  /** Leave for good: the posts, the communities, the promises, the account. See below. */
  eraseMe(): Promise<unknown>;
}

export interface AccountProps {
  client: AccountClient;
  /** The signed-in reader's name, or null when nobody is. */
  name: string | null;
  /** Told when the session is gone, so the app can forget who it thought was here. */
  onSignedOut(): void;
  /** Told when the reader renames themselves, so every row that shows a name catches up. */
  onRenamed(name: string): void;
}

/*
 * WHO IS SIGNED IN, AND HOW TO LEAVE.
 *
 * The server has answered `POST /auth/logout` since identity was built and no client ever called
 * it, so **there was no way to sign out of this product**. Nor any way to see whose account was
 * open: signing in appears only as an inline prompt inside a feature ("sign in to take part"), and
 * once you have, the product never mentions you again.
 *
 * That is worst on a shared machine — a family computer, a library, a phone passed around after a
 * study — where the next person inherits an account that can post under someone else's name. It is
 * also the plainest thing a product can get wrong about trust, which is the currency this whole
 * layer runs on (D21).
 *
 * IT RENDERS NOTHING FOR A SIGNED-OUT READER, which is most of them and all of the first visit.
 * The design principle here is fewest controls visible: an account bar that says "not signed in" is
 * a permanent piece of furniture answering a question nobody asked.
 */
export function Account({ client, name, onSignedOut, onRenamed }: AccountProps): React.JSX.Element | null {
  const { t } = useLang();
  const [leaving, setLeaving] = useState(false);
  /** The name being typed, or null while nobody is changing it — see the control below. */
  const [renaming, setRenaming] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  /** Whether the reader has asked to leave and is being shown what that takes. */
  const [erasing, setErasing] = useState(false);

  if (name === null) return null;

  const signOut = async (): Promise<void> => {
    setLeaving(true);
    try {
      await client.signOut();
      onSignedOut();
    } catch {
      /*
       * Deliberately still reported as signed out. The cookie is httpOnly, so this call is the only
       * thing that can clear it — but a reader who pressed "sign out" and is told it failed has no
       * next move, and leaving the UI claiming they are still signed in is the more alarming lie of
       * the two. The next request will discover the truth either way.
       */
      onSignedOut();
    } finally {
      setLeaving(false);
    }
  };

  /*
   * A NAME OF THEIR OWN — §5.1's promise, which had no mechanism until 2026-08-18.
   *
   * The name came from Google at first sign-in and nothing could change it, so a legal name went
   * into the voice token every room reads, into every Hall roster, the pending queue, the ban list
   * and the moderation log. In a product about where traditions disagree, a pseudonym is not a
   * preference — it is the difference between arguing in public and being findable for it.
   *
   * Behind one press rather than always on screen: this is a rare act, and a text box sitting in
   * the account bar for ever is furniture answering a question nobody asked, which is the same rule
   * that keeps this control invisible to a signed-out reader.
   */
  /*
   * THE RIGHT TO LEAVE, which no screen could exercise.
   *
   * `DELETE /auth/me` has been answered since the identity plane was built, and it does careful
   * work: the reader's posts, their Discord channels, their studies — with a retraction sent to any
   * community that had been told about one — the Halls they own and everything those were holding,
   * then the reader and their cookie. Rooms are deliberately left standing, because other people
   * are in them, and the reader is taken out of every one that names them.
   *
   * No client had a method for it, so all of that existed and could be reached from **nowhere**:
   * the one act this product owes somebody who wants to stop being part of it. This is the same
   * absence that hid `logout`, `unbanFromRoom` and the reports queue, and the same way of finding
   * it — asking which routes no client calls.
   *
   * TWO PRESSES, NOT A TYPED WORD. The Hall's deletion asks a leader to type the address, which
   * works because the address is theirs and on the screen. A word to type here would have to be an
   * English one in a product that reads in three languages, or a name that is not unique — so
   * instead the second button says exactly what it does, in whatever language the reader is in, and
   * the sentence beside it says what goes.
   */
  const erase = async (): Promise<void> => {
    setProblem(null);
    try {
      await client.eraseMe();
      /* The session went with the account, so the app must forget them: anything else leaves a page
         addressing somebody who no longer exists. */
      onSignedOut();
    } catch (caught) {
      setProblem(refusalWords(t, caught, 'auth.eraseFailed'));
      setErasing(false);
    }
  };

  const rename = async (): Promise<void> => {
    const wanted = (renaming ?? '').trim();
    if (!wanted) return;
    setProblem(null);
    try {
      const renamed = await client.renameMe(wanted);
      onRenamed(renamed.displayName);
      setRenaming(null);
    } catch (caught) {
      /* The server's own words — "that is not a name" is the actionable half, in the reader's
         language where the refusal has a name and in the server's English where it has not. */
      setProblem(refusalWords(t, caught, 'auth.renameFailed'));
    }
  };

  return (
    <p className="account">
      <span className="account-name">{t('auth.signedInAs', { name })}</span>
      {renaming === null ? (
        <button type="button" className="account-rename" onClick={() => setRenaming(name)}>
          {t('auth.rename')}
        </button>
      ) : (
        <>
          <input
            type="text"
            className="account-name-input"
            value={renaming}
            onChange={event => setRenaming(event.target.value)}
            aria-label={t('auth.renameLabel')}
            maxLength={DISPLAY_NAME_MAX}
          />
          <button type="button" className="account-rename-save" onClick={() => void rename()} disabled={!renaming.trim()}>
            {t('auth.renameSave')}
          </button>
          <button type="button" className="account-rename-cancel" onClick={() => { setRenaming(null); setProblem(null); }}>
            {t('auth.renameCancel')}
          </button>
        </>
      )}
      <button type="button" className="account-sign-out" onClick={() => void signOut()} disabled={leaving}>
        {t('auth.signOut')}
      </button>
      {/* Offered plainly rather than hidden: a reader who wants to go should not have to hunt, and
          a right nobody can find is the state this control was built to end. What it costs them is
          spelled out before anything happens. */}
      {!erasing ? (
        <button type="button" className="account-erase" onClick={() => { setErasing(true); setProblem(null); }}>
          {t('auth.erase')}
        </button>
      ) : (
        <>
          <span className="account-erase-what" role="status">{t('auth.eraseWhat')}</span>
          <button type="button" className="account-erase-yes" onClick={() => void erase()}>
            {t('auth.eraseYes')}
          </button>
          <button type="button" className="account-erase-cancel" onClick={() => setErasing(false)}>
            {t('auth.eraseCancel')}
          </button>
        </>
      )}
      {problem && <span className="account-problem" role="alert">{problem}</span>}
    </p>
  );
}
